import { WidgetContainer } from '@/components/analytics/WidgetContainer';
import type { QuizletPerformance, MasteryLevel, QuizletMastery } from '@/types/analytics';
import styles from './QuizletMasteryHeatmap.module.scss';

export interface QuizletMasteryHeatmapProps {
  data: (QuizletPerformance | QuizletMastery)[] | null;
  isLoading?: boolean;
  error?: Error | null;
}

/**
 * Quizlet Mastery Heatmap Widget
 *
 * Grid visualization showing mastery level for each quizlet.
 * Color-coded by Bronze/Silver/Gold mastery tiers.
 */
export function QuizletMasteryHeatmap({ data, isLoading, error }: QuizletMasteryHeatmapProps) {
  const isEmpty = !data || data.length === 0;

  // Get color for mastery level
  const getMasteryColor = (level: MasteryLevel): string => {
    switch (level) {
      case 'gold':
        return 'var(--success)';
      case 'silver':
        return 'var(--warning)';
      case 'bronze':
        return 'var(--error)';
      default:
        return 'var(--text-tertiary)';
    }
  };

  // Get medal emoji for mastery level
  const getMasteryEmoji = (level: MasteryLevel): string => {
    switch (level) {
      case 'gold':
        return '🥇';
      case 'silver':
        return '🥈';
      case 'bronze':
        return '🥉';
      default:
        return '⚪';
    }
  };

  // Calculate mastery distribution
  const masteryCount = data
    ? {
      gold: data.filter((q) => q.masteryLevel === 'gold').length,
      silver: data.filter((q) => q.masteryLevel === 'silver').length,
      bronze: data.filter((q) => q.masteryLevel === 'bronze').length,
      unrated: data.filter((q) => q.masteryLevel === 'unrated').length,
    }
    : { gold: 0, silver: 0, bronze: 0, unrated: 0 };

  return (
    <WidgetContainer
      title="Quizlet Mastery Overview"
      description="Mastery levels across all quizlets"
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage="Practice quizlets to track mastery levels"
      size="large"
    >
      <div className={styles.masteryHeatmap}>
        {/* Grid */}
        <div className={styles.masteryHeatmap__grid}>
          {data?.map((quizlet) => (
            <div
              key={quizlet.quizletId}
              className={styles.masteryHeatmap__cell}
              style={{ borderColor: getMasteryColor(quizlet.masteryLevel) }}
              title={`${quizlet.quizletName}: ${quizlet.successRate.toFixed(0)}% (${quizlet.masteryLevel})`}
            >
              <span className={styles.masteryHeatmap__cellEmoji}>
                {getMasteryEmoji(quizlet.masteryLevel)}
              </span>
              <span className={styles.masteryHeatmap__cellName}>
                {quizlet.quizletName}
              </span>
              <span className={styles.masteryHeatmap__cellRate}>
                {quizlet.successRate.toFixed(0)}%
              </span>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className={styles.masteryHeatmap__legend}>
          <div className={styles.masteryHeatmap__legendItem}>
            <span className={styles.masteryHeatmap__legendIcon}>🥇</span>
            <span className={styles.masteryHeatmap__legendLabel}>
              Gold ({masteryCount.gold})
            </span>
            <span className={styles.masteryHeatmap__legendValue}>&gt;90%</span>
          </div>
          <div className={styles.masteryHeatmap__legendItem}>
            <span className={styles.masteryHeatmap__legendIcon}>🥈</span>
            <span className={styles.masteryHeatmap__legendLabel}>
              Silver ({masteryCount.silver})
            </span>
            <span className={styles.masteryHeatmap__legendValue}>70-90%</span>
          </div>
          <div className={styles.masteryHeatmap__legendItem}>
            <span className={styles.masteryHeatmap__legendIcon}>🥉</span>
            <span className={styles.masteryHeatmap__legendLabel}>
              Bronze ({masteryCount.bronze})
            </span>
            <span className={styles.masteryHeatmap__legendValue}>&lt;70%</span>
          </div>
        </div>
      </div>
    </WidgetContainer>
  );
}
